import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NumericInput from '../components/common/NumericInput';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useClassSettingsModal } from '../contexts/ClassSettingsModalContext';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import {
  deleteCalendarDay,
  getAcademicYearSettings,
  getCalendarDays,
  dedupeHolidaysByDay,
  getKoreanAutoHolidays,
  upsertAcademicYearSettings,
  upsertCalendarDay,
} from '../services/calendarService';
import {
  getAcademicCalendarSections,
  type PageSectionLayout,
  saveAcademicCalendarSections,
} from '../services/sectionLayoutService';
import { useTeamIdForLeaderImport } from '../hooks/useTeamIdForLeaderImport';
import { useCalendarLinkedAcademicYear } from '../hooks/useCalendarLinkedAcademicYear';
import { ensureFixedProgramsFromAnnualTimetable } from '../utils/fixedProgramsFromAnnualCells';
import AcademicCalendarLeaderSectionImportModal from '../components/academicCalendar/AcademicCalendarLeaderSectionImportModal';
import type { TeamLeaderAcademicCalendarImportPayload } from '../services/academicCalendarTeamLeaderImportService';
import {
  ANNUAL_PREREQUISITE_REFRESH_EVENT,
  emitAnnualPrerequisiteRefresh,
  type AnnualPrerequisiteRefreshDetail,
} from '../utils/annualPrerequisiteRefresh';
import {
  type AcademicSectionCalendarSettings,
  clearAcademicSectionLocalStorage,
  defaultAcademicSectionSettings,
  emitAcademicSectionCalendarChange,
  readAcademicSectionCalendarDaysFromLocalStorage,
  readAcademicSectionSettingsFromLocalStorage,
  writeAcademicSectionCalendarDaysToLocalStorage,
  writeAcademicSectionSettingsToLocalStorage,
  stripAutoBreakVacationCalendarDays,
} from '../utils/calendarSectionAcademicYear';
import { clampCalendarHolidayNote, limitHolidayNoteInput, MAX_CALENDAR_HOLIDAY_NOTE_CHARS } from '../utils/calendarHolidayNote';
import {
  addDays,
  countDays,
  countWeekdays,
  formatDateKor,
  formatDateShort,
  isWeekday,
  isWeekend,
  parseISODate,
  toISODate,
} from '../utils/date';
import { CalendarDay, Grade } from '../types/domain';
import AiPresetImportModal, { type AiCalendarApply } from '../components/aiPreset/AiPresetImportModal';
import AiPresetOpenButton from '../components/aiPreset/AiPresetOpenButton';
import FixedProgramSection from '../components/aiPreset/FixedProgramSection';
import type { FixedProgramBulkHandle } from '../components/aiPreset/FixedProgramBulkTable';
import { gradeNumbersFromClassName, guessSchoolYear } from '../utils/aiPresetExtract';
import { newDraftId, updateFixedDrafts } from '../utils/fixedProgramDrafts';

function parseClassName(raw: string): { title: string; grades: number[] } {
  const [base, rest] = raw.split('::');
  if (!rest) return { title: raw, grades: [] };
  const nums = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title: base, grades: nums };
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** `YYYY-MM-DD`만 월·일을 유지한 채 연도를 `deltaYears`만큼 이동 */
function shiftIsoDateByYears(iso: string, deltaYears: number): string {
  const t = iso.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t) || deltaYears === 0) return t;
  const d = parseISODate(t);
  if (Number.isNaN(d.getTime())) return t;
  d.setFullYear(d.getFullYear() + deltaYears);
  return toISODate(d);
}

/** 섹션 순서 드래그용 래퍼 - Translate만 사용해 드래그 중 섹션 크기 유지 */
const SortableSectionGroup: React.FC<{
  id: string;
  children: (attributes: any, listeners: any) => React.ReactNode;
}> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: 'section' },
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={'cp-grade-track-section-group' + (isDragging ? ' cp-section-dragging' : '')}
    >
      {children(attributes, listeners)}
    </div>
  );
};

type CalendarSection = PageSectionLayout;

const MAX_SECTIONS = 6;

type SectionCalendarSettings = AcademicSectionCalendarSettings;

function holidayDayInputStorageKey(workspaceId: string): string {
  return `cp:academic-calendar:holiday-day-input:${workspaceId}`;
}

interface AcademicCalendarPageProps {
  workspaceId: string;
  currentGradeId?: string;
  grades?: Grade[];
  onNavGradeChange?: (gradeId: string) => void;
}

const todayYear = new Date().getFullYear();

const AcademicCalendarPage: React.FC<AcademicCalendarPageProps> = ({
  workspaceId,
  currentGradeId = '',
  grades = [],
  onNavGradeChange,
}) => {
  const [academicYear] = useState(todayYear);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [dayInput, setDayInput] = useState(() => {
    if (typeof window === 'undefined' || !workspaceId) return '';
    try {
      return localStorage.getItem(holidayDayInputStorageKey(workspaceId)) ?? '';
    } catch {
      return '';
    }
  });
  const [note, setNote] = useState('');
  const [weekendClassDayInput, setWeekendClassDayInput] = useState('');
  const [weekendClassNote, setWeekendClassNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionSettingsHydrated, setSectionSettingsHydrated] = useState(false);
  const [snapshotReady, setSnapshotReady] = useState(false);
  const { toastState: saveToastState, showSaving, showSuccess, showError, showInfo } = useAutoStatusToast(3000);
  const [editingDay, setEditingDay] = useState<CalendarDay | null>(null);
  const [editDayDate, setEditDayDate] = useState('');
  const [editDayNote, setEditDayNote] = useState('');
  const [pendingDeleteDay, setPendingDeleteDay] = useState<CalendarDay | null>(null);

  const [sections, setSections] = useState<CalendarSection[]>(() => [{ id: 'base', gradeIds: [] }]);
  const [sectionSettings, setSectionSettings] = useState<Record<string, SectionCalendarSettings>>(() => ({}));
  /** 클래스 섹션별 공휴일·토일 수업일 (base는 calendarDays 사용) */
  const [sectionCalendarDays, setSectionCalendarDays] = useState<Record<string, CalendarDay[]>>({});
  const [classPickerSectionId, setClassPickerSectionId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [pendingDeleteSectionId, setPendingDeleteSectionId] = useState<string | null>(null);
  const [removeClassContext, setRemoveClassContext] = useState<{
    sectionId: string;
    gradeId: string;
  } | null>(null);
  const [deleteSectionContext, setDeleteSectionContext] = useState<string | null>(null);
  const [resetSectionContext, setResetSectionContext] = useState<string | null>(null);
  const [copySectionContext, setCopySectionContext] = useState<string | null>(null);
  const teamIdForLeaderImport = useTeamIdForLeaderImport(workspaceId);
  const fixedProgramGradeIds = useMemo(() => grades.map((g) => g.id).filter(Boolean), [grades]);
  const fixedProgramYear = useCalendarLinkedAcademicYear(
    workspaceId,
    currentGradeId || fixedProgramGradeIds[0] || ''
  );
  useEffect(() => {
    if (!workspaceId) return;
    void ensureFixedProgramsFromAnnualTimetable({
      workspaceId,
      academicYear: fixedProgramYear,
      gradeIds: fixedProgramGradeIds,
    });
  }, [workspaceId, fixedProgramYear, fixedProgramGradeIds]);
  const [leaderImportTargetSectionId, setLeaderImportTargetSectionId] = useState<string | null>(null);
  /** 복사 모달에서 선택한 클래스(복수) - 복사 시 새 섹션의 gradeIds로 사용 */
  const [copyModalSelectedGradeIds, setCopyModalSelectedGradeIds] = useState<string[]>([]);
  /** 섹션 복사 시 sections 반영 후 나머지 데이터를 적용하기 위한 ref */
  const pendingCopyDataRef = useRef<{
    newId: string;
    settings: SectionCalendarSettings;
    days: CalendarDay[];
  } | null>(null);

  const setDayInputPersist = useCallback(
    (value: string) => {
      setDayInput(value);
      if (!workspaceId) return;
      try {
        if (value) {
          localStorage.setItem(holidayDayInputStorageKey(workspaceId), value);
        } else {
          localStorage.removeItem(holidayDayInputStorageKey(workspaceId));
        }
      } catch {
        /* ignore quota / private mode */
      }
    },
    [workspaceId]
  );

  useEffect(() => {
    if (!workspaceId) return;
    try {
      setDayInput(localStorage.getItem(holidayDayInputStorageKey(workspaceId)) ?? '');
    } catch {
      setDayInput('');
    }
  }, [workspaceId]);

  const { setAcademicCalendarDirty, saveAcademicCalendarRef } = useContext(GradeTrackLeaveContext);
  const classSettingsModal = useClassSettingsModal();

  const classPickerAvailableGrades = useMemo(() => {
    if (!classPickerSectionId) return [];
    return grades.filter((g) => {
      const sec = sections.find((s) => s.id === classPickerSectionId);
      const inThis = sec?.gradeIds.includes(g.id);
      const inAny = sections.some(
        (s) => s.id !== classPickerSectionId && s.gradeIds.includes(g.id)
      );
      return !inThis && !inAny;
    });
  }, [grades, sections, classPickerSectionId]);

  const lastSavedSnapshotRef = useRef<string>('');
  const getSnapshotRef = useRef<() => string>(() => '');
  const syncSnapshotAfterSaveRef = useRef(false);
  /**
   * 저장 직후 emit이 자기 리스너를 다시 돌리면, 스냅샷 정렬 이후에 서버 재로드 결과가 들어와
   * isDirty가 다시 true가 된다(저장 토스트 후에도 이탈 경고).
   */
  const skipSelfPrerequisiteRefreshOnceRef = useRef(false);
  const [, setSnapshotAlignNonce] = useState(0);

  const getRawDaysForSection = (sectionId: string): CalendarDay[] =>
    sectionId === 'base' ? calendarDays : (sectionCalendarDays[sectionId] ?? []);

  const getSnapshot = useCallback(
    () =>
      JSON.stringify({
        sectionSettings,
        calendarDays,
        sectionCalendarDays,
        sections,
      }),
    [sectionSettings, calendarDays, sectionCalendarDays, sections]
  );
  getSnapshotRef.current = getSnapshot;
  const isCalendarDirty =
    snapshotReady && lastSavedSnapshotRef.current !== '' && getSnapshot() !== lastSavedSnapshotRef.current;
  /** 섹션 안 고정(행사) 프로그램 표 — 통합 저장으로만 저장 */
  const fixedProgramRefs = useRef<Record<string, FixedProgramBulkHandle | null>>({});
  const [fixedProgramDirty, setFixedProgramDirty] = useState<Record<string, boolean>>({});
  const fixedDirtyHandlers = useRef<Record<string, (d: boolean) => void>>({});
  const fixedDirtyHandlerFor = (sid: string) =>
    (fixedDirtyHandlers.current[sid] ||= (d: boolean) =>
      setFixedProgramDirty((prev) => (!!prev[sid] === d ? prev : { ...prev, [sid]: d })));
  const isFixedDirty = Object.values(fixedProgramDirty).some(Boolean);
  const isDirty = isCalendarDirty || isFixedDirty;

  useEffect(() => {
    if (loading || sectionsLoading || !sectionSettingsHydrated || snapshotReady) return;
    let timeoutId: number | null = null;
    const rafId = requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        lastSavedSnapshotRef.current = getSnapshotRef.current();
        setSnapshotReady(true);
      }, 0);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [loading, sectionsLoading, sectionSettingsHydrated, snapshotReady]);

  useEffect(() => {
    setAcademicCalendarDirty(isDirty);
    return () => setAcademicCalendarDirty(false);
  }, [isDirty, setAcademicCalendarDirty]);

  useLayoutEffect(() => {
    if (loading || !syncSnapshotAfterSaveRef.current) return;
    lastSavedSnapshotRef.current = getSnapshotRef.current();
    syncSnapshotAfterSaveRef.current = false;
    setSnapshotAlignNonce((n) => n + 1);
  }, [loading, calendarDays, sectionSettings, sectionCalendarDays, sections]);

  const saveSections = useCallback(
    (next: CalendarSection[]) => {
      setSections(next);
    },
    []
  );

  const getSectionSettings = useCallback((sectionId: string): SectionCalendarSettings => {
    const s = sectionSettings[sectionId];
    if (s) return s;
    if (sectionId === 'base') {
      const y = Number(academicYear);
      const year = !Number.isNaN(y) && y >= 2000 && y <= 2100 ? y : todayYear;
      return sectionSettings.base ?? defaultAcademicSectionSettings(year);
    }
    return defaultAcademicSectionSettings(todayYear);
  }, [academicYear, sectionSettings]);

  const getDaysForSection = useCallback(
    (sectionId: string): CalendarDay[] =>
      stripAutoBreakVacationCalendarDays(getRawDaysForSection(sectionId)),
    [calendarDays, sectionCalendarDays]
  );

  const updateSectionSettings = (sectionId: string, patch: Partial<SectionCalendarSettings>) => {
    const prev = getSectionSettings(sectionId);
    let next = { ...prev, ...patch };
    if (
      typeof patch.academicYear === 'number' &&
      Number.isFinite(patch.academicYear) &&
      patch.academicYear !== prev.academicYear
    ) {
      const delta = patch.academicYear - prev.academicYear;
      const dateKeys: (keyof SectionCalendarSettings)[] = [
        'yearStartsOn',
        'yearEndsOn',
        'summerBreakStartsOn',
        'summerBreakEndsOn',
        'winterBreakStartsOn',
        'winterBreakEndsOn',
      ];
      const shifted: Partial<SectionCalendarSettings> = {};
      dateKeys.forEach((k) => {
        const oldVal = prev[k];
        if (typeof oldVal === 'string' && oldVal.trim()) {
          (shifted as Record<string, string>)[k] = shiftIsoDateByYears(oldVal, delta);
        }
      });
      next = { ...next, ...shifted };
    }
    setSectionSettings((curr) => ({ ...curr, [sectionId]: next }));
    if (sectionId !== 'base') {
      writeAcademicSectionSettingsToLocalStorage(workspaceId, sectionId, next);
    }
  };

  const addSection = () => {
    if (sections.length >= MAX_SECTIONS) return;
    const newId = generateId();
    const base = sectionSettings['base'];
    const y = base?.academicYear ?? academicYear ?? todayYear;
    const year = Number.isNaN(Number(y)) ? todayYear : Number(y);
    const initial = defaultAcademicSectionSettings(year);
    setSectionSettings((curr) => ({ ...curr, [newId]: initial }));
    writeAcademicSectionSettingsToLocalStorage(workspaceId, newId, initial);
    setSectionCalendarDays((prev) => ({ ...prev, [newId]: [] }));
    saveSections([...sections, { id: newId, gradeIds: [] }]);
  };

  const handleCopySectionConfirm = () => {
    const sourceId = copySectionContext;
    if (!sourceId || sections.length >= MAX_SECTIONS) {
      setCopySectionContext(null);
      setCopyModalSelectedGradeIds([]);
      return;
    }
    if (copyModalSelectedGradeIds.length === 0) return;
    const srcSection = sections.find((s) => s.id === sourceId);
    if (!srcSection) {
      setCopySectionContext(null);
      setCopyModalSelectedGradeIds([]);
      return;
    }
    const newId = generateId();
    const srcIndex = sections.findIndex((s) => s.id === sourceId);
    const newSection: CalendarSection = { id: newId, gradeIds: [...copyModalSelectedGradeIds] };
    const nextSections = [...sections];
    nextSections.splice(srcIndex + 1, 0, newSection);

    const srcSettings = getSectionSettings(sourceId);
    const srcDays = sectionCalendarDays[sourceId] ?? getDaysForSection(sourceId);
    const copiedDays =
      srcDays.length > 0
        ? srcDays.map((d) => ({ ...d, id: generateId() }))
        : [];

    pendingCopyDataRef.current = {
      newId,
      settings: { ...srcSettings },
      days: copiedDays,
    };
    saveSections(nextSections);
    setCopySectionContext(null);
    setCopyModalSelectedGradeIds([]);
  };

  const [aiSectionId, setAiSectionId] = useState<string | null>(null);
  const [aiFixedSectionId, setAiFixedSectionId] = useState<string | null>(null);

  /** AI 자료 입력 반영: 비어 있지 않은 설정만 덮어쓰고, 같은 날짜는 교체·나머지 기존 일자는 유지. 행사는 고정 프로그램 일괄 관리로 */
  const applyAiCalendar = (sectionId: string, r: AiCalendarApply) => {
    const cur = getSectionSettings(sectionId);
    const pick = (v: string, old: string) => (v && v.trim() ? v : old);
    const nextSettings: SectionCalendarSettings = {
      ...cur,
      academicYear: r.settings.academicYear || cur.academicYear,
      yearStartsOn: pick(r.settings.yearStartsOn, cur.yearStartsOn),
      yearEndsOn: pick(r.settings.yearEndsOn, cur.yearEndsOn),
      summerBreakStartsOn: pick(r.settings.summerBreakStartsOn, cur.summerBreakStartsOn),
      summerBreakEndsOn: pick(r.settings.summerBreakEndsOn, cur.summerBreakEndsOn),
      winterBreakStartsOn: pick(r.settings.winterBreakStartsOn, cur.winterBreakStartsOn),
      winterBreakEndsOn: pick(r.settings.winterBreakEndsOn, cur.winterBreakEndsOn),
    };
    setSectionSettings((curr) => ({ ...curr, [sectionId]: nextSettings }));
    if (sectionId !== 'base') writeAcademicSectionSettingsToLocalStorage(workspaceId, sectionId, nextSettings);
    const newDates = new Set(r.days.map((d) => d.day));
    const omitted = new Set(r.omittedDays ?? []);
    const currentDays = sectionId === 'base' ? calendarDays : (sectionCalendarDays[sectionId] ?? []);
    const merged: CalendarDay[] = [
      ...currentDays.filter((d) => {
        if (d.day_kind === 'class') return true;
        if (omitted.has(d.day) || newDates.has(d.day)) return false;
        return true;
      }),
      ...r.days.map((d) => ({
        id: generateId(),
        workspace_id: workspaceId,
        day: d.day,
        day_kind: d.day_kind,
        note: clampCalendarHolidayNote(d.note) || null,
      })),
    ];
    if (sectionId === 'base') setCalendarDays(merged);
    else {
      setSectionCalendarDays((prev) => ({ ...prev, [sectionId]: merged }));
      writeAcademicSectionCalendarDaysToLocalStorage(workspaceId, sectionId, merged);
    }
    showSuccess(
      `AI 자료를 반영했습니다(휴일·휴업일 ${r.days.length}건). 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.`
    );
  };

  const aiDefaultGradeFor = (sectionId: string): number => {
    const sec = sections.find((x) => x.id === sectionId);
    const ids = sec?.gradeIds ?? [];
    const gid = ids.includes(currentGradeId) ? currentGradeId : ids[0];
    const g = grades.find((x) => x.id === gid) ?? grades.find((x) => x.id === currentGradeId);
    return gradeNumbersFromClassName(g?.name ?? '')[0] ?? 1;
  };
  const classOptionsFor = (ids: string[]) =>
    ids
      .map((id) => grades.find((g) => g.id === id))
      .filter((g): g is Grade => !!g)
      .map((g) => {
        const parsed = parseClassName(g.name);
        return { id: g.id, label: parsed.title, grades: parsed.grades.map(String) };
      });

  const applyLeaderCalendarImport = (sectionId: string, payload: TeamLeaderAcademicCalendarImportPayload) => {
    const importedSettings: SectionCalendarSettings = {
      academicYear: payload.settings.academic_year,
      yearStartsOn: payload.settings.year_starts_on,
      yearEndsOn: payload.settings.year_ends_on,
      summerBreakStartsOn: payload.settings.summer_break_starts_on,
      summerBreakEndsOn: payload.settings.summer_break_ends_on,
      winterBreakStartsOn: payload.settings.winter_break_starts_on ?? '',
      winterBreakEndsOn: payload.settings.winter_break_ends_on ?? '',
    };
    const importedDays: CalendarDay[] = payload.days.map((d) => ({
      id: generateId(),
      workspace_id: workspaceId,
      day: d.day,
      day_kind: d.day_kind,
      note: d.note,
    }));

    /** 사이드바에서 선택된 클래스가 비어 있고 다른 섹션에 없으면 이 섹션에 자동 등록 */
    const focusGradeId = currentGradeId.trim();
    const canAutoRegister =
      !!focusGradeId &&
      grades.some((g) => g.id === focusGradeId) &&
      !sections.some((s) => s.gradeIds.includes(focusGradeId));
    if (canAutoRegister) {
      saveSections(
        sections.map((s) =>
          s.id === sectionId ? { ...s, gradeIds: [...s.gradeIds, focusGradeId] } : s
        )
      );
    }

    setSectionSettings((curr) => ({ ...curr, [sectionId]: importedSettings }));
    if (sectionId !== 'base') {
      writeAcademicSectionSettingsToLocalStorage(workspaceId, sectionId, importedSettings);
    }

    if (sectionId === 'base') {
      setCalendarDays(importedDays);
    } else {
      setSectionCalendarDays((prev) => ({ ...prev, [sectionId]: importedDays }));
      writeAcademicSectionCalendarDaysToLocalStorage(workspaceId, sectionId, importedDays);
    }
    showSuccess('팀장 학사일정을 가져왔습니다. 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.');
  };

  useEffect(() => {
    const pending = pendingCopyDataRef.current;
    if (!pending || !sections.some((s) => s.id === pending.newId)) return;
    pendingCopyDataRef.current = null;
    setSectionSettings((curr) => ({ ...curr, [pending.newId]: pending.settings }));
    setSectionCalendarDays((prev) => ({ ...prev, [pending.newId]: pending.days }));
    writeAcademicSectionSettingsToLocalStorage(workspaceId, pending.newId, pending.settings);
    writeAcademicSectionCalendarDaysToLocalStorage(workspaceId, pending.newId, pending.days);
  }, [sections, workspaceId]);

  /** 복사 모달에서 사용 가능한 클래스 = 아직 어떤 섹션에도 등록되지 않은 클래스 */
  const copyModalAvailableGrades = grades.filter(
    (g) => !sections.some((s) => s.gradeIds.includes(g.id))
  );
  const toggleCopyModalGrade = (gradeId: string) => {
    setCopyModalSelectedGradeIds((prev) =>
      prev.includes(gradeId) ? prev.filter((id) => id !== gradeId) : [...prev, gradeId]
    );
  };

  const handleSectionDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (active.data.current?.type !== 'section') return;
    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(sections, oldIndex, newIndex);
    saveSections(next);
  };

  const removeSection = (sectionId: string) => {
    if (sections.length <= 1) return;
    saveSections(sections.filter((s) => s.id !== sectionId));
    setSectionCalendarDays((prev) => {
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    setSectionSettings((prev) => {
      if (!(sectionId in prev)) return prev;
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
    clearAcademicSectionLocalStorage(workspaceId, sectionId);
    if (editingSectionId === sectionId) {
      setEditingDay(null);
      setEditingSectionId(null);
    }
    if (pendingDeleteSectionId === sectionId) {
      setPendingDeleteDay(null);
      setPendingDeleteSectionId(null);
    }
    setDeleteSectionContext(null);
  };

  /** 섹션 본문·등록 클래스 초기화. DB는 저장하기 시에만 반영 */
  const resetSectionData = (sectionId: string) => {
    const year =
      sectionSettings[sectionId]?.academicYear ??
      sectionSettings.base?.academicYear ??
      todayYear;
    const defaults = defaultAcademicSectionSettings(
      Number.isFinite(Number(year)) ? Number(year) : todayYear
    );
    setSectionSettings((curr) => ({ ...curr, [sectionId]: defaults }));
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, gradeIds: [] } : s))
    );
    if (sectionId === 'base') {
      setCalendarDays([]);
    } else {
      setSectionCalendarDays((prev) => ({ ...prev, [sectionId]: [] }));
      writeAcademicSectionSettingsToLocalStorage(workspaceId, sectionId, defaults);
      writeAcademicSectionCalendarDaysToLocalStorage(workspaceId, sectionId, []);
    }
    if (editingSectionId === sectionId) {
      setEditingDay(null);
      setEditingSectionId(null);
    }
    if (pendingDeleteSectionId === sectionId) {
      setPendingDeleteDay(null);
      setPendingDeleteSectionId(null);
    }
    setWeekendClassDayInput('');
    setWeekendClassNote('');
    setResetSectionContext(null);
  };

  const addClassToSection = (sectionId: string, gradeId: string) => {
    const next = sections.map((s) =>
      s.id === sectionId && !s.gradeIds.includes(gradeId)
        ? { ...s, gradeIds: [...s.gradeIds, gradeId] }
        : s
    );
    saveSections(next);
    setClassPickerSectionId(null);
    onNavGradeChange?.(gradeId);
  };

  const removeClassFromSection = (sectionId: string, gradeId: string) => {
    saveSections(
      sections.map((s) =>
        s.id === sectionId
          ? { ...s, gradeIds: s.gradeIds.filter((id) => id !== gradeId) }
          : s
      )
    );
    setRemoveClassContext(null);
  };

  const load = useCallback(async () => {
    const y = Number(academicYear);
    const def = !Number.isNaN(y) && y >= 2000 && y <= 2100
      ? defaultAcademicSectionSettings(y)
      : defaultAcademicSectionSettings(todayYear);
    const row = await getAcademicYearSettings(workspaceId, academicYear).catch(() => null);
    let from = def.yearStartsOn;
    let to = def.yearEndsOn;
    const baseSettings: SectionCalendarSettings = row
      ? {
          academicYear: row.academic_year,
          yearStartsOn: row.year_starts_on,
          yearEndsOn: row.year_ends_on,
          summerBreakStartsOn: row.summer_break_starts_on,
          summerBreakEndsOn: row.summer_break_ends_on,
          winterBreakStartsOn: row.winter_break_starts_on ?? '',
          winterBreakEndsOn: row.winter_break_ends_on ?? '',
        }
      : def;
    from = baseSettings.yearStartsOn;
    to = baseSettings.yearEndsOn;
    setSectionSettings((curr) => ({ ...curr, base: baseSettings }));
    try {
      const days = await getCalendarDays(workspaceId, from, to);
      setCalendarDays(
        stripAutoBreakVacationCalendarDays(
          days.map((d) =>
            d.day_kind !== 'class' && d.note
              ? { ...d, note: clampCalendarHolidayNote(d.note) }
              : d
          )
        )
      );
    } catch {
      setCalendarDays([]);
    }
  }, [workspaceId, academicYear]);

  useEffect(() => {
    if (!workspaceId) return;
    lastSavedSnapshotRef.current = '';
    setSnapshotReady(false);
    /* 학년도만 바뀌는 경우 sections가 그대로라 비(base) LS 하이드레이트 effect가 다시 안 돌아가므로
       sectionSettingsHydrated를 false로 두면 스냅샷 정렬이 영원히 막혀 isDirty가 항상 false가 된다. */
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [workspaceId, academicYear, load]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setSectionsLoading(true);
    getAcademicCalendarSections(workspaceId)
      .then((loadedSections) => {
        if (cancelled) return;
        setSections(loadedSections);
      })
      .catch(() => {
        if (cancelled) return;
        setSections([{ id: 'base', gradeIds: [] }]);
      })
      .finally(() => {
        if (cancelled) return;
        setSectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  /** 연간시간표 전체 가져오기 등: keep-alive 상태에서도 서버 기준으로 다시 맞춤 */
  useEffect(() => {
    if (!workspaceId) return;
    const onRefresh = (event: Event) => {
      const detail = (event as CustomEvent<AnnualPrerequisiteRefreshDetail>).detail;
      if (!detail || detail.workspaceId !== workspaceId) return;
      if (!detail.scopes.includes('academic_calendar')) return;
      if (skipSelfPrerequisiteRefreshOnceRef.current) {
        skipSelfPrerequisiteRefreshOnceRef.current = false;
        return;
      }
      setSectionsLoading(true);
      setLoading(true);
      void getAcademicCalendarSections(workspaceId, { skipLocalCache: true })
        .then((loadedSections) => {
          setSections(loadedSections);
          const nextSettings: Record<string, SectionCalendarSettings> = {};
          const nextDays: Record<string, CalendarDay[]> = {};
          loadedSections.forEach((section) => {
            if (section.academicCalendarSettings) {
              nextSettings[section.id] = section.academicCalendarSettings;
            }
            if (section.id !== 'base' && section.academicCalendarDays) {
              nextDays[section.id] = section.academicCalendarDays;
            }
          });
          if (Object.keys(nextSettings).length > 0) {
            setSectionSettings((curr) => ({ ...curr, ...nextSettings }));
          }
          setSectionCalendarDays((curr) => {
            const kept: Record<string, CalendarDay[]> = {};
            Object.keys(curr).forEach((id) => {
              if (loadedSections.some((s) => s.id === id)) kept[id] = curr[id];
            });
            return { ...kept, ...nextDays };
          });
          setSectionSettingsHydrated(true);
        })
        .catch(() => {
          /* keep current */
        })
        .finally(() => setSectionsLoading(false));
      void load().finally(() => setLoading(false));
    };
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onRefresh);
  }, [workspaceId, load]);

  useEffect(() => {
    if (!workspaceId || typeof window === 'undefined') return;
    setSectionSettingsHydrated(false);
    const loadedSettings: Record<string, SectionCalendarSettings> = {};
    const loadedDays: Record<string, CalendarDay[]> = {};
    sections.forEach((s) => {
      if (s.id === 'base') return;
      const parsed =
        readAcademicSectionSettingsFromLocalStorage(workspaceId, s.id) ?? s.academicCalendarSettings;
      if (parsed) loadedSettings[s.id] = parsed;
      const lsDays = readAcademicSectionCalendarDaysFromLocalStorage(workspaceId, s.id);
      const days = lsDays.length > 0 ? lsDays : (s.academicCalendarDays ?? []);
      if (days.length > 0) loadedDays[s.id] = days;
    });
    if (Object.keys(loadedSettings).length > 0) {
      setSectionSettings((curr) => ({ ...curr, ...loadedSettings }));
    }
    if (Object.keys(loadedDays).length > 0) {
      setSectionCalendarDays((curr) => ({ ...curr, ...loadedDays }));
    }
    setSectionSettingsHydrated(true);

    /** 비(base) 섹션: LS에만 있던 학사 본문을 DB JSON에 올림.
     * 반드시 현재 로컬 섹션 목록만 대상으로 함 — 서버에만 남은 삭제 섹션을 다시 쓰지 않음. */
    void (async () => {
      try {
        const localSections = sections;
        const localIds = new Set(localSections.map((s) => s.id));
        const server = await getAcademicCalendarSections(workspaceId, { skipLocalCache: true });
        const serverById = new Map(server.map((s) => [s.id, s]));
        /** 로컬에서 섹션을 줄인 뒤(아직 저장 전) 서버 목록을 다시 쓰면 삭제가 되살아남 → 백필 중단 */
        if (server.some((s) => !localIds.has(s.id))) return;
        let changed = false;
        const merged: PageSectionLayout[] = localSections.map((local) => {
          const s = serverById.get(local.id);
          if (local.id === 'base') {
            return {
              id: local.id,
              gradeIds: local.gradeIds,
              academicCalendarSettings: s?.academicCalendarSettings,
              academicCalendarDays: s?.academicCalendarDays,
            };
          }
          const lsSettings =
            readAcademicSectionSettingsFromLocalStorage(workspaceId, local.id) ??
            loadedSettings[local.id] ??
            local.academicCalendarSettings ??
            s?.academicCalendarSettings;
          const lsDaysRaw = readAcademicSectionCalendarDaysFromLocalStorage(workspaceId, local.id);
          const lsDays =
            lsDaysRaw.length > 0
              ? lsDaysRaw
              : loadedDays[local.id] ?? local.academicCalendarDays ?? s?.academicCalendarDays ?? [];
          const serverMissingSettings = !s?.academicCalendarSettings?.academicYear;
          const serverMissingDays = !(s?.academicCalendarDays && s.academicCalendarDays.length > 0);
          const localHasSettings = !!lsSettings?.academicYear;
          const localHasDays = lsDays.length > 0;
          if ((localHasSettings && serverMissingSettings) || (localHasDays && serverMissingDays)) {
            changed = true;
            return {
              id: local.id,
              gradeIds: local.gradeIds,
              academicCalendarSettings: lsSettings,
              academicCalendarDays: stripAutoBreakVacationCalendarDays(lsDays),
            };
          }
          return {
            id: local.id,
            gradeIds: local.gradeIds,
            academicCalendarSettings: s?.academicCalendarSettings ?? lsSettings,
            academicCalendarDays: s?.academicCalendarDays ?? stripAutoBreakVacationCalendarDays(lsDays),
          };
        });
        if (changed) {
          await saveAcademicCalendarSections(workspaceId, merged);
          merged.forEach((section) => {
            if (section.id === 'base') return;
            if (section.academicCalendarSettings) {
              writeAcademicSectionSettingsToLocalStorage(
                workspaceId,
                section.id,
                section.academicCalendarSettings
              );
            }
            if (section.academicCalendarDays) {
              writeAcademicSectionCalendarDaysToLocalStorage(
                workspaceId,
                section.id,
                section.academicCalendarDays
              );
            }
          });
        }
      } catch {
        /* ignore sync failures */
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load section settings when section ids change
  }, [workspaceId, sections.length, sections.map((s) => s.id).join(',')]);

  /** base는 테이블이 소스이지만, 팀원 가져오기는 섹션 JSON을 쓰므로 로드 후 JSON 스냅샷을 맞춤(수업시간 패턴) */
  const baseJsonSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    baseJsonSyncedRef.current = null;
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || loading || !sectionSettingsHydrated) return;
    const base = sectionSettings.base;
    if (!base?.academicYear || !base.yearStartsOn) return;
    if (baseJsonSyncedRef.current === workspaceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const server = await getAcademicCalendarSections(workspaceId, { skipLocalCache: true });
        const baseSec = server.find((s) => s.id === 'base');
        if (!baseSec) return;
        const missingSettings = !baseSec.academicCalendarSettings?.academicYear;
        const missingDays = !(baseSec.academicCalendarDays && baseSec.academicCalendarDays.length > 0);
        const yearMismatch = baseSec.academicCalendarSettings?.academicYear !== base.academicYear;
        if (!missingSettings && !missingDays && !yearMismatch) {
          if (!cancelled) baseJsonSyncedRef.current = workspaceId;
          return;
        }
        /** 로컬 섹션 목록 기준으로만 병합 — 서버에만 있는 삭제된 섹션을 되살리지 않음 */
        const localSectionIds = new Set(sections.map((s) => s.id));
        if (server.some((s) => !localSectionIds.has(s.id))) {
          /** 저장 전 로컬 삭제 상태면 base JSON 백필도 미룸(저장하기에서 반영) */
          return;
        }
        const merged: PageSectionLayout[] = sections.map((s) =>
          s.id === 'base'
            ? {
                id: 'base',
                gradeIds: s.gradeIds,
                academicCalendarSettings: base,
                academicCalendarDays: stripAutoBreakVacationCalendarDays(calendarDays),
              }
            : {
                id: s.id,
                gradeIds: s.gradeIds,
                academicCalendarSettings:
                  sectionSettings[s.id] ??
                  s.academicCalendarSettings ??
                  server.find((row) => row.id === s.id)?.academicCalendarSettings,
                academicCalendarDays:
                  sectionCalendarDays[s.id] ??
                  s.academicCalendarDays ??
                  server.find((row) => row.id === s.id)?.academicCalendarDays,
              }
        );
        if (cancelled) return;
        await saveAcademicCalendarSections(workspaceId, merged);
        baseJsonSyncedRef.current = workspaceId;
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    workspaceId,
    loading,
    sectionSettingsHydrated,
    sectionSettings.base,
    sectionSettings,
    sectionCalendarDays,
    calendarDays,
    sections,
  ]);

  useEffect(() => {
    if (!workspaceId) return;
    Object.entries(sectionCalendarDays).forEach(([sectionId, days]) => {
      if (sectionId === 'base') return;
      writeAcademicSectionCalendarDaysToLocalStorage(workspaceId, sectionId, days);
    });
  }, [workspaceId, sectionCalendarDays]);

  /** 학사일정은 항상 전체 섹션을 보여 주고, 선택 클래스 구독 여부는 사이드 메뉴 원형으로 표시 */
  const visibleSections = sections;

  const computeForSection = useCallback(
    (settings: SectionCalendarSettings, daysForSection: CalendarDay[]) => {
      const {
        yearStartsOn: ys,
        yearEndsOn: ye,
        summerBreakStartsOn: ss,
        summerBreakEndsOn: se,
        winterBreakStartsOn: ws,
        winterBreakEndsOn: we,
      } = settings;
      if (!ys || !ye || !ss || !se) return null;
      const term1End = addDays(parseISODate(ss), -1);
      const term1EndISO = toISODate(term1End);
      const term2Start = addDays(parseISODate(se), 1);
      const term2StartISO = toISODate(term2Start);
      const inRange = (day: string, from: string, to: string) =>
        day >= from && day <= to;
      const term1Holidays = daysForSection.filter(
        (d) =>
          inRange(d.day, ys, term1EndISO) &&
          isWeekday(d.day) &&
          d.day_kind !== 'class'
      ).length;
      const term2Holidays = daysForSection.filter((d) => {
        if (!inRange(d.day, term2StartISO, ye)) return false;
        if (!isWeekday(d.day)) return false;
        if (d.day_kind === 'class') return false;
        if (ws && we && inRange(d.day, ws, we)) return false;
        return true;
      }).length;
      const term1WeekendClass = daysForSection.filter(
        (d) =>
          d.day_kind === 'class' &&
          isWeekend(d.day) &&
          inRange(d.day, ys, term1EndISO)
      ).length;
      const term2WeekendClass = daysForSection.filter((d) => {
        if (d.day_kind !== 'class' || !isWeekend(d.day)) return false;
        if (!inRange(d.day, term2StartISO, ye)) return false;
        if (ws && we && inRange(d.day, ws, we)) return false;
        return true;
      }).length;
      let term1Days = countWeekdays(ys, term1EndISO) - term1Holidays + term1WeekendClass;
      let term2Days = countWeekdays(term2StartISO, ye) - term2Holidays + term2WeekendClass;
      if (ws && we) {
        const winterDays = countWeekdays(ws, we);
        term2Days = Math.max(0, term2Days - winterDays);
      }
      term1Days = Math.max(0, term1Days);
      term2Days = Math.max(0, term2Days);
      return {
        term1Start: ys,
        term1End: term1EndISO,
        term2Start: term2StartISO,
        term2End: ye,
        term1Days,
        term2Days,
        totalDays: term1Days + term2Days,
        summerBreakStartsOn: ss,
        summerBreakEndsOn: se,
        winterBreakStartsOn: ws,
        winterBreakEndsOn: we,
      };
    },
    []
  );

  const handleDiscardUnsaved = useCallback(() => {
    const raw = lastSavedSnapshotRef.current;
    if (!raw) return;
    try {
      const snap = JSON.parse(raw) as {
        sectionSettings: Record<string, SectionCalendarSettings>;
        calendarDays: CalendarDay[];
        sectionCalendarDays: Record<string, CalendarDay[]>;
        sections: CalendarSection[];
      };
      setSectionSettings(snap.sectionSettings ?? {});
      setCalendarDays(snap.calendarDays ?? []);
      setSectionCalendarDays(snap.sectionCalendarDays ?? {});
      setSections(
        snap.sections && snap.sections.length > 0 ? snap.sections : [{ id: 'base', gradeIds: [] }]
      );
    } catch {
      return;
    }
    setEditingDay(null);
    setEditDayDate('');
    setEditDayNote('');
    setPendingDeleteDay(null);
    setClassPickerSectionId(null);
    setEditingSectionId(null);
    setPendingDeleteSectionId(null);
    setRemoveClassContext(null);
    setDeleteSectionContext(null);
    setCopySectionContext(null);
    setCopyModalSelectedGradeIds([]);
    setLeaderImportTargetSectionId(null);
  }, []);

  const handleSaveAll = useCallback(async () => {
    const base = getSectionSettings('base');
    if (!base.yearStartsOn || !base.yearEndsOn || !base.summerBreakStartsOn || !base.summerBreakEndsOn) {
      showInfo('학년도 시작일·종료일, 여름방학 시작일·종료일은 필수입니다.');
      return;
    }
    const y = Number(base.academicYear);
    if (Number.isNaN(y) || y < 2000 || y > 2100) {
      showInfo('학년도를 올바르게 입력하세요.');
      return;
    }
    setLoading(true);
    showSaving();
    try {
      await upsertAcademicYearSettings(workspaceId, {
        academicYear: y,
        yearStartsOn: base.yearStartsOn,
        yearEndsOn: base.yearEndsOn,
        summerBreakStartsOn: base.summerBreakStartsOn,
        summerBreakEndsOn: base.summerBreakEndsOn,
        winterBreakStartsOn: base.winterBreakStartsOn || null,
        winterBreakEndsOn: base.winterBreakEndsOn || null,
      });
      const r = { from: base.yearStartsOn, to: base.yearEndsOn };
      const strippedBaseDays = stripAutoBreakVacationCalendarDays(calendarDays);
      const serverDays = await getCalendarDays(workspaceId, r.from, r.to);
      const localDaySet = new Set(strippedBaseDays.map((d) => d.day));
      for (const d of serverDays) {
        if (!localDaySet.has(d.day)) {
          await deleteCalendarDay(workspaceId, d.day);
        }
      }
      for (const d of strippedBaseDays) {
        await upsertCalendarDay(workspaceId, {
          day: d.day,
          dayKind: d.day_kind,
          note: d.note ?? undefined,
        });
      }
      setCalendarDays(strippedBaseDays);
      /** 수업시간 가져오기와 같이, 섹션 JSON에 본문(설정·일수)을 함께 저장 — base 포함 */
      const sectionsToSave: PageSectionLayout[] = sections.map((section) =>
        section.id === 'base'
          ? {
              id: section.id,
              gradeIds: section.gradeIds,
              academicCalendarSettings: getSectionSettings('base'),
              academicCalendarDays: strippedBaseDays,
            }
          : {
              ...section,
              academicCalendarSettings: getSectionSettings(section.id),
              academicCalendarDays: stripAutoBreakVacationCalendarDays(sectionCalendarDays[section.id] ?? []),
            }
      );
      await saveAcademicCalendarSections(workspaceId, sectionsToSave);
      /** invalidate가 섹션 LS를 지울 수 있어 저장 직후 다시 기록 */
      sectionsToSave.forEach((section) => {
        if (section.id === 'base') return;
        if (section.academicCalendarSettings) {
          writeAcademicSectionSettingsToLocalStorage(workspaceId, section.id, section.academicCalendarSettings);
        }
        if (section.academicCalendarDays) {
          writeAcademicSectionCalendarDaysToLocalStorage(
            workspaceId,
            section.id,
            section.academicCalendarDays
          );
        }
      });
      emitAcademicSectionCalendarChange(workspaceId, 'base');
      skipSelfPrerequisiteRefreshOnceRef.current = true;
      emitAnnualPrerequisiteRefresh(workspaceId, ['academic_calendar']);
      syncSnapshotAfterSaveRef.current = true;
      setSnapshotReady(true);
      showSuccess();
    } catch (error: any) {
      showError(error?.message ?? '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [
    calendarDays,
    getSectionSettings,
    workspaceId,
    sections,
    sectionCalendarDays,
    showError,
    showInfo,
    showSaving,
    showSuccess,
  ]);

  /** 통합 저장: 학사일정 + 섹션별 고정(행사) 프로그램 표 */
  const handleSaveAllWithFixed = useCallback(async () => {
    if (isCalendarDirty) await handleSaveAll();
    let savedFixed = false;
    Object.values(fixedProgramRefs.current).forEach((h) => {
      if (h?.isDirty()) {
        h.save();
        savedFixed = true;
      }
    });
    if (savedFixed && !isCalendarDirty) showSuccess();
  }, [isCalendarDirty, handleSaveAll, showSuccess]);
  const handleDiscardAll = useCallback(() => {
    handleDiscardUnsaved();
    Object.values(fixedProgramRefs.current).forEach((h) => h?.discard());
  }, [handleDiscardUnsaved]);

  useEffect(() => {
    saveAcademicCalendarRef.current = { save: handleSaveAllWithFixed, discard: handleDiscardAll };
    return () => {
      saveAcademicCalendarRef.current = null;
    };
  }, [saveAcademicCalendarRef, handleSaveAllWithFixed, handleDiscardAll]);

  const handleAddCalendarDay = (sectionId: string) => {
    if (!dayInput) return;
    const newDay: CalendarDay = {
      id: generateId(),
      workspace_id: workspaceId,
      day: dayInput,
      day_kind: 'holiday',
      note: clampCalendarHolidayNote(note) || null,
    };
    if (sectionId === 'base') {
      setCalendarDays((prev) => [...prev, newDay]);
    } else {
      setSectionCalendarDays((prev) => ({
        ...prev,
        [sectionId]: [...(prev[sectionId] ?? []), newDay],
      }));
    }
    setNote('');
  };

  const handleRegisterKoreanHolidays = (sectionId: string) => {
    const settings = getSectionSettings(sectionId);
    const { yearStartsOn: ys, yearEndsOn: ye } = settings;
    if (!ys || !ye) {
      showInfo('학년도 시작일·종료일을 먼저 설정해 주세요.');
      return;
    }
    const [y1] = ys.split('-').map(Number);
    const [y2] = ye.split('-').map(Number);
    const years = y1 === y2 ? [y1] : [y1, y2];
    const all: { day: string; note: string }[] = [];
    years.forEach((y) => all.push(...getKoreanAutoHolidays(y)));
    const merged = dedupeHolidaysByDay(all);
    const inRange = merged.filter((h) => h.day >= ys && h.day <= ye);
    const currentDays = sectionId === 'base' ? calendarDays : (sectionCalendarDays[sectionId] ?? []);
    const existingDays = new Set(currentDays.map((d) => d.day));
    const toAdd = inRange.filter((h) => !existingDays.has(h.day));
    if (toAdd.length === 0) {
      showInfo('해당 구간에 추가할 공휴일이 없습니다.');
      return;
    }
    const newDays: CalendarDay[] = toAdd.map((h) => ({
      id: generateId(),
      workspace_id: workspaceId,
      day: h.day,
      day_kind: 'holiday',
      note: clampCalendarHolidayNote(h.note) || null,
    }));
    if (sectionId === 'base') {
      setCalendarDays((prev) => [...prev, ...newDays]);
    } else {
      setSectionCalendarDays((prev) => ({
        ...prev,
        [sectionId]: [...(prev[sectionId] ?? []), ...newDays],
      }));
    }
    showInfo(`공휴일 ${toAdd.length}건을 로컬에 추가했습니다. 저장하기를 누르면 서버에 반영됩니다.`);
  };

  const openEditDay = (d: CalendarDay, sectionId: string) => {
    setEditingDay(d);
    setEditingSectionId(sectionId);
    setEditDayDate(d.day);
    setEditDayNote(d.day_kind === 'class' ? (d.note ?? '') : clampCalendarHolidayNote(d.note ?? ''));
  };

  const handleSaveEditDay = () => {
    if (!editingDay || !editDayDate.trim() || !editingSectionId) return;
    const sid = editingSectionId;
    if (sid === 'base') {
      setCalendarDays((prev) => {
        const next = prev.filter((d) => d.day !== editingDay.day);
        next.push({
          ...editingDay,
          day: editDayDate.trim(),
          note:
            editingDay.day_kind === 'class'
              ? editDayNote.trim() || null
              : clampCalendarHolidayNote(editDayNote) || null,
        });
        return next;
      });
    } else {
      setSectionCalendarDays((prev) => {
        const list = prev[sid] ?? [];
        const next = list.filter((d) => d.day !== editingDay.day);
        next.push({
          ...editingDay,
          day: editDayDate.trim(),
          note:
            editingDay.day_kind === 'class'
              ? editDayNote.trim() || null
              : clampCalendarHolidayNote(editDayNote) || null,
        });
        return { ...prev, [sid]: next };
      });
    }
    setEditingDay(null);
    setEditingSectionId(null);
  };

  const handleDeleteDay = (d: CalendarDay) => {
    const sid = pendingDeleteSectionId ?? 'base';
    if (sid === 'base') {
      setCalendarDays((prev) => prev.filter((x) => x.day !== d.day));
    } else {
      setSectionCalendarDays((prev) => ({
        ...prev,
        [sid]: (prev[sid] ?? []).filter((x) => x.day !== d.day),
      }));
    }
    setPendingDeleteDay(null);
    setPendingDeleteSectionId(null);
  };

  const handleAddWeekendClassDay = (sectionId: string) => {
    if (!weekendClassDayInput) return;
    const newDay: CalendarDay = {
      id: generateId(),
      workspace_id: workspaceId,
      day: weekendClassDayInput,
      day_kind: 'class',
      note: weekendClassNote.trim() || null,
    };
    if (sectionId === 'base') {
      setCalendarDays((prev) => [...prev, newDay]);
    } else {
      setSectionCalendarDays((prev) => ({
        ...prev,
        [sectionId]: [...(prev[sectionId] ?? []), newDay],
      }));
    }
    setWeekendClassDayInput('');
    setWeekendClassNote('');
  };

  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  return (
    <section className="cp-timetable-hub-page">
      <div className="cp-page-title-row">
        <div>
          <h2>학사일정</h2>
        </div>
        <button
          type="button"
          className="cp-btn-save-all"
          onClick={() => void handleSaveAllWithFixed()}
          disabled={loading || !isDirty}
        >
          {isDirty ? '저장하기' : '변경사항 없음'}
        </button>
      </div>
      <DndContext
        sensors={sectionSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleSectionDragEnd}
      >
        <SortableContext
          items={sections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {visibleSections.map((section) => {
            const gradeIds = section.gradeIds;
            const visibleGradeIds = gradeIds;
            const settings = getSectionSettings(section.id);
            const sectionDays = getDaysForSection(section.id);
            const sectionComputed = computeForSection(settings, sectionDays);
            const sectionDisabled = false;
            return (
              <SortableSectionGroup key={section.id} id={section.id}>
                {(dragAttributes, dragListeners) => (
                  <div className="cp-academic-calendar-group">
                    <div className="cp-track-section-header cp-track-section-header-with-class cp-grade-track-header-row cp-section-mhead">
                      <div className="card-summary pill cp-track-class-pill">
                        클래스 공유
                      </div>
                      <h3>학사일정 수업일 설정</h3>
                      <button
                        type="button"
                        className="cp-program-color-class-add-btn"
                        onClick={() => setClassPickerSectionId(section.id)}
                        aria-label="클래스 등록"
                        title="클래스 등록"
                      >
                        + 클래스 등록
                      </button>
                      {gradeIds.length === 0 ? (
                        <span className="cp-section-class-empty-hint">
                          * 데이터를 먼저 입력한 뒤 클래스를 등록할 수 있습니다.
                        </span>
                      ) : (
                      visibleGradeIds.map((gid) => {
                        const grade = grades.find((g) => g.id === gid);
                        const parsed = grade ? parseClassName(grade.name) : { title: gid, grades: [] };
                        return (
                          <div
                            key={gid}
                            className={
                              'cp-grade-track-class-block' +
                              (gid === currentGradeId ? ' is-nav-focus' : ' is-nav-muted')
                            }
                          >
                            <span className="cp-grade-track-class-block-label">{parsed.title}</span>
                            {parsed.grades.map((num) => (
                              <span key={num} className="class-grade-chip-display">
                                {num}
                              </span>
                            ))}
                            <button
                              type="button"
                              className="cp-top-right-remove-btn"
                              onClick={() =>
                                setRemoveClassContext({ sectionId: section.id, gradeId: gid })
                              }
                              aria-label="클래스 제거"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })
                      )}
                      <div className="cp-grade-track-section-header-actions">
                        <AiPresetOpenButton onClick={() => setAiSectionId(section.id)} />
                        <button
                          type="button"
                          className="cp-program-color-section-copy-btn"
                          onClick={() => setResetSectionContext(section.id)}
                          aria-label="섹션 초기화"
                          title="섹션 초기화"
                        >
                          <img src="/reset.svg" alt="" className="track-icon" />
                        </button>
                        <button
                          type="button"
                          className="cp-program-color-section-copy-btn"
                          onClick={() => {
                            setCopySectionContext(section.id);
                            setCopyModalSelectedGradeIds([]);
                          }}
                          aria-label="섹션 복사"
                          title="섹션 복사"
                        >
                          <img src="/Copy.svg" alt="복사" className="track-icon" />
                        </button>
                        {teamIdForLeaderImport ? (
                          <button
                            type="button"
                            className="cp-program-color-section-copy-btn"
                            onClick={() => setLeaderImportTargetSectionId(section.id)}
                            aria-label="팀장 학사일정 가져오기"
                            title="팀장 학사일정 가져오기"
                          >
                            <img src="/Download.svg" alt="" className="track-icon" />
                          </button>
                        ) : null}
                        {sections.length >= 2 && (
                          <>
                            <button
                              type="button"
                              className="cp-program-color-section-delete-btn"
                              onClick={() => setDeleteSectionContext(section.id)}
                              aria-label="섹션 삭제"
                              title="섹션 삭제"
                            >
                              <img src="/Trash.svg" alt="삭제" className="track-icon" />
                            </button>
                            <button
                              type="button"
                              className="cp-program-color-section-grip-btn"
                              aria-label="순서 변경"
                              title="드래그하여 순서 변경"
                              {...dragAttributes}
                              {...dragListeners}
                            >
                              <img src="/GripVertical.svg" alt="" className="track-icon" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                <div className="cp-section-body">
                <div className="academic-calendar-two-col">
                  <article className="cp-card academic-calendar-settings-card">
                    <h3>학사일정 등록</h3>
                    <div className="form-row academic-year-form">
                      <label className="academic-year-label">
                        <NumericInput
                          min={2000}
                          max={2100}
                          maxLength={4}
                          value={String(settings.academicYear)}
                          onChange={(v) => {
                            const val = v === '' ? todayYear : parseInt(v, 10) || todayYear;
                            updateSectionSettings(section.id, { academicYear: val });
                          }}
                          defaultEmptyValue={String(todayYear)}
                          disabled={sectionDisabled}
                        />
                      <span>학년도</span>
                    </label>
                  </div>
                    <div className="form-row two-col">
                      <label>
                        학년도 시작일(시업식) <span className="required">*필수</span>
                        <input
                          type="date"
                          value={settings.yearStartsOn}
                          onChange={(e) =>
                            updateSectionSettings(section.id, { yearStartsOn: e.target.value })
                          }
                          disabled={sectionDisabled}
                        />
                      </label>
                      <label>
                        학년도 종료일(종업식/졸업식) <span className="required">*필수</span>
                        <input
                          type="date"
                          value={settings.yearEndsOn}
                          onChange={(e) =>
                            updateSectionSettings(section.id, { yearEndsOn: e.target.value })
                          }
                          disabled={sectionDisabled}
                        />
                      </label>
                    </div>
                    <div className="form-row two-col">
                      <label>
                        여름방학 시작일 <span className="required">*필수</span>
                        <input
                          type="date"
                          value={settings.summerBreakStartsOn}
                          onChange={(e) =>
                            updateSectionSettings(section.id, {
                              summerBreakStartsOn: e.target.value,
                            })
                          }
                          disabled={sectionDisabled}
                        />
                      </label>
                      <label>
                        여름방학 종료일 <span className="required">*필수</span>
                        <input
                          type="date"
                          value={settings.summerBreakEndsOn}
                          onChange={(e) =>
                            updateSectionSettings(section.id, {
                              summerBreakEndsOn: e.target.value,
                            })
                          }
                          disabled={sectionDisabled}
                        />
                      </label>
                    </div>
                    <div className="form-row two-col">
                      <div className="academic-optional-date-field">
                        <span className="academic-optional-date-head">
                          <span>겨울방학 시작일 <span className="optional">*선택</span></span>
                          <button
                            type="button"
                            className="cp-btn-secondary academic-date-clear"
                            disabled={sectionDisabled || !settings.winterBreakStartsOn}
                            onClick={() => updateSectionSettings(section.id, { winterBreakStartsOn: '' })}
                          >
                            지우기
                          </button>
                        </span>
                        <input
                          type="date"
                          value={settings.winterBreakStartsOn}
                          onChange={(e) =>
                            updateSectionSettings(section.id, {
                              winterBreakStartsOn: e.target.value,
                            })
                          }
                          disabled={sectionDisabled}
                          aria-label="겨울방학 시작일"
                        />
                      </div>
                      <div className="academic-optional-date-field">
                        <span className="academic-optional-date-head">
                          <span>겨울방학 종료일 <span className="optional">*선택</span></span>
                          <button
                            type="button"
                            className="cp-btn-secondary academic-date-clear"
                            disabled={sectionDisabled || !settings.winterBreakEndsOn}
                            onClick={() => updateSectionSettings(section.id, { winterBreakEndsOn: '' })}
                          >
                            지우기
                          </button>
                        </span>
                        <input
                          type="date"
                          value={settings.winterBreakEndsOn}
                          onChange={(e) =>
                            updateSectionSettings(section.id, {
                              winterBreakEndsOn: e.target.value,
                            })
                          }
                          disabled={sectionDisabled}
                          aria-label="겨울방학 종료일"
                        />
                      </div>
                    </div>
                  </article>
                <div className="academic-calendar-right-col">
                  <article className="cp-card academic-calendar-holiday-card">
                    <div className="holiday-card-title-row">
                      <h3>공휴일 및 휴업일 등록</h3>
                      <button
                        type="button"
                        className="cp-btn-secondary holiday-auto-register-btn"
                        onClick={() => handleRegisterKoreanHolidays(section.id)}
                        disabled={loading || !settings.yearStartsOn || !settings.yearEndsOn || sectionDisabled}
                      >
                        공휴일 자동 등록
                      </button>
                    </div>
                    <div className="form-row holiday-register-row">
                      <input
                        type="date"
                        value={dayInput}
                        onChange={(e) => setDayInputPersist(e.target.value)}
                        placeholder="날짜"
                        disabled={sectionDisabled}
                      />
                      <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(limitHolidayNoteInput(e.target.value))}
                        placeholder={`메모 (예: 3.1절, 최대 ${MAX_CALENDAR_HOLIDAY_NOTE_CHARS}자)`}
                        maxLength={MAX_CALENDAR_HOLIDAY_NOTE_CHARS}
                        disabled={sectionDisabled}
                      />
                      <button
                        type="button"
                        onClick={() => handleAddCalendarDay(section.id)}
                        disabled={loading || sectionDisabled}
                      >
                        등록
                      </button>
                    </div>
                    <div className="calendar-day-blocks">
                      {[...sectionDays]
                        .filter((d) => d.day_kind !== 'class')
                        .sort((a, b) => a.day.localeCompare(b.day))
                        .map((d) => (
                          <div key={d.id} className="calendar-day-block">
                            <span>
                              {formatDateShort(d.day)}
                              {d.note ? ` ${d.note}` : ''}
                            </span>
                            <span className="calendar-day-block-actions">
                              <button
                                type="button"
                                className="track-icon-button"
                                onClick={() => openEditDay(d, section.id)}
                                aria-label="수정"
                                disabled={sectionDisabled}
                              >
                                <img src="/edit.svg" alt="수정" className="track-icon" />
                              </button>
                              <button
                                type="button"
                                className="track-icon-button"
                                onClick={() => {
                                  setPendingDeleteDay(d);
                                  setPendingDeleteSectionId(section.id);
                                }}
                                aria-label="삭제"
                                disabled={sectionDisabled}
                              >
                                <img src="/Trash.svg" alt="삭제" className="track-icon" />
                              </button>
                            </span>
                          </div>
                        ))}
                    </div>
                  </article>
                  <article className="cp-card academic-calendar-weekend-class-card">
                    <div className="card-title-row card-title-row-with-desc">
                      <h3>토요일, 일요일 수업일 등록</h3>
                      <span className="card-title-desc">해당 날짜를 수업일로 포함합니다.</span>
                    </div>
                    <div className="form-row holiday-register-row">
                      <input
                        type="date"
                        value={weekendClassDayInput}
                        onChange={(e) => setWeekendClassDayInput(e.target.value)}
                        placeholder="날짜"
                        disabled={sectionDisabled}
                      />
                      <input
                        type="text"
                        value={weekendClassNote}
                        onChange={(e) => setWeekendClassNote(e.target.value)}
                        placeholder="메모 (예: 보강)"
                        disabled={sectionDisabled}
                      />
                      <div className="holiday-register-buttons">
                        <button
                          onClick={() => handleAddWeekendClassDay(section.id)}
                          disabled={loading || sectionDisabled}
                        >
                          등록
                        </button>
                      </div>
                    </div>
                    <div className="calendar-day-blocks calendar-day-blocks-weekend-class">
                      {(() => {
                        const weekendClassDays = sectionDays.filter(
                          (d) => d.day_kind === 'class' && isWeekend(d.day)
                        );
                        return weekendClassDays.length === 0 ? (
                          <p className="calendar-day-blocks-empty">등록된 토·일 수업일이 없습니다.</p>
                        ) : (
                          [...weekendClassDays]
                            .sort((a, b) => a.day.localeCompare(b.day))
                            .map((d) => (
                              <div key={d.id} className="calendar-day-block calendar-day-block-class">
                                <span>
                                  {formatDateShort(d.day)}
                                  {d.note ? ` ${d.note}` : ''}
                                </span>
                                <span className="calendar-day-block-actions">
                                  <button
                                    type="button"
                                    className="track-icon-button"
                                    onClick={() => openEditDay(d, section.id)}
                                    aria-label="수정"
                                    disabled={sectionDisabled}
                                  >
                                    <img src="/edit.svg" alt="수정" className="track-icon" />
                                  </button>
                                  <button
                                    type="button"
                                    className="track-icon-button"
                                    onClick={() => {
                                      setPendingDeleteDay(d);
                                      setPendingDeleteSectionId(section.id);
                                    }}
                                    aria-label="삭제"
                                    disabled={sectionDisabled}
                                  >
                                    <img src="/Trash.svg" alt="삭제" className="track-icon" />
                                  </button>
                                </span>
                              </div>
                            ))
                        );
                      })()}
                    </div>
                  </article>
                </div>
              </div>
              </div>
              {sectionComputed && (
                <div className="academic-summary academic-summary-bottom academic-summary-two-col">
                  <div className="academic-summary-row">
                    <p className="summary-line">
                      1학기 : {formatDateKor(sectionComputed.term1Start)} ~ {formatDateKor(sectionComputed.term1End)}{' '}
                      (수업일 수 : {sectionComputed.term1Days}일)
                    </p>
                    <p className="summary-line summary-line-sub">
                      여름방학 : {formatDateKor(sectionComputed.summerBreakStartsOn)} ~ {formatDateKor(sectionComputed.summerBreakEndsOn)}{' '}
                      (여름방학일 수 : {countDays(sectionComputed.summerBreakStartsOn, sectionComputed.summerBreakEndsOn)}일)
                    </p>
                  </div>
                  <div className="academic-summary-row">
                    <p className="summary-line">
                      2학기 : {formatDateKor(sectionComputed.term2Start)} ~ {formatDateKor(sectionComputed.term2End)}{' '}
                      (수업일 수 : {sectionComputed.term2Days}일)
                    </p>
                    <p className="summary-line summary-line-sub">
                      {sectionComputed.winterBreakStartsOn && sectionComputed.winterBreakEndsOn ? (
                        <>
                          겨울방학 : {formatDateKor(sectionComputed.winterBreakStartsOn)} ~ {formatDateKor(sectionComputed.winterBreakEndsOn)}{' '}
                          (겨울방학일 수 : {countDays(sectionComputed.winterBreakStartsOn, sectionComputed.winterBreakEndsOn)}일)
                        </>
                      ) : (
                        '겨울방학 : 겨울방학이 없습니다.'
                      )}
                    </p>
                  </div>
                  <p className="summary-line total academic-summary-full">총 수업일 수 : {sectionComputed.totalDays}일</p>
                </div>
              )}
              <FixedProgramSection
                ref={(h) => {
                  fixedProgramRefs.current[section.id] = h;
                }}
                onDirtyChange={fixedDirtyHandlerFor(section.id)}
                onOpenAi={() => setAiFixedSectionId(section.id)}
                sectionKey={`${workspaceId}:${section.id}`}
                workspaceId={workspaceId}
                classOptions={classOptionsFor(section.gradeIds)}
                filterGradeIds={section.gradeIds}
                includeDateRange={
                  settings.yearStartsOn && settings.yearEndsOn
                    ? { start: settings.yearStartsOn, end: settings.yearEndsOn }
                    : undefined
                }
                seedFromCellCache={false}
              />
            </div>
                  )}
                </SortableSectionGroup>
              );
            })}
        </SortableContext>
      </DndContext>

      {sections.length < MAX_SECTIONS && (
        <button
          type="button"
          className="cp-program-color-add-section-btn"
          onClick={addSection}
          aria-label="섹션 추가"
        >
          <span className="cp-program-color-add-section-btn-icon">+</span>
          <span className="cp-program-color-add-section-btn-label">섹션 추가</span>
        </button>
      )}


      {aiSectionId ? (
        <AiPresetImportModal
          kind="calendar"
          title="AI 자료 입력 · 학사일정"
          resumeKey={`calendar:${workspaceId}:${aiSectionId}`}
          defaultGrade={aiDefaultGradeFor(aiSectionId)}
          schoolYear={getSectionSettings(aiSectionId).academicYear || guessSchoolYear()}
          autoHolidays={(y) => dedupeHolidaysByDay(getKoreanAutoHolidays(y)).map((h) => ({ day: h.day, note: clampCalendarHolidayNote(h.note) }))}
          classOptions={classOptionsFor(sections.find((x) => x.id === aiSectionId)?.gradeIds ?? [])}
          onClose={() => setAiSectionId(null)}
          onApply={(r) => applyAiCalendar(aiSectionId, r)}
        />
      ) : null}

      {aiFixedSectionId ? (
        <AiPresetImportModal
          kind="fixedPrograms"
          title="AI 자료 입력 · 고정(행사) 프로그램"
          resumeKey={`fixedPrograms:${workspaceId}:${aiFixedSectionId}`}
          defaultGrade={aiDefaultGradeFor(aiFixedSectionId)}
          schoolYear={getSectionSettings(aiFixedSectionId).academicYear || guessSchoolYear()}
          workspaceId={workspaceId}
          classOptions={classOptionsFor(grades.map((g) => g.id))}
          onClose={() => setAiFixedSectionId(null)}
          onApply={(rows) => {
            const incoming = rows
              .filter((row) => !row.deleted && (row.name.trim() || row.date))
              .map((row) => ({ ...row, id: newDraftId(), source: 'ai' as const, updatedAt: Date.now() }));
            if (incoming.length) updateFixedDrafts(workspaceId, (prev) => [...prev, ...incoming]);
            setAiFixedSectionId(null);
            showSuccess(
              incoming.length
                ? `고정 프로그램 ${incoming.length}건을 반영했습니다. 표에서 확인한 뒤 오른쪽 위 "저장하기"를 누르면 서버에 반영됩니다.`
                : '반영할 고정 프로그램이 없습니다.'
            );
          }}
        />
      ) : null}

      {/* 섹션 복사 모달 - 적용할 클래스 복수 선택 후 복사 */}
      {copySectionContext && (
        <div
          className="cp-modal-backdrop"
          onClick={() => {
            setCopySectionContext(null);
            setCopyModalSelectedGradeIds([]);
          }}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 복사</h3>
            <p className="page-desc">
              복사할 섹션 데이터를 적용받을 클래스를 선택하세요.
            </p>
            <div className="cp-program-color-class-picker-list">
              {copyModalAvailableGrades.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={`cp-program-color-class-picker-item ${copyModalSelectedGradeIds.includes(g.id) ? 'cp-program-color-class-picker-item-selected' : ''}`}
                  onClick={() => toggleCopyModalGrade(g.id)}
                >
                  {parseClassName(g.name).title}
                </button>
              ))}
            </div>
            {copyModalAvailableGrades.length === 0 && (
              <p className="page-desc">등록할 클래스가 없기 때문에 섹션 복사를 진행할 수 없습니다.</p>
            )}
            {copyModalAvailableGrades.length > 0 && copyModalSelectedGradeIds.length === 0 && (
              <p className="page-desc">하나 이상의 클래스를 선택해 주세요.</p>
            )}
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setCopySectionContext(null);
                  setCopyModalSelectedGradeIds([]);
                }}
              >
                취소하기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleCopySectionConfirm}
                disabled={
                  sections.length >= MAX_SECTIONS ||
                  copyModalAvailableGrades.length === 0 ||
                  copyModalSelectedGradeIds.length === 0
                }
              >
                복사하기
              </button>
            </div>
          </div>
        </div>
      )}

      {leaderImportTargetSectionId && teamIdForLeaderImport ? (
        <AcademicCalendarLeaderSectionImportModal
          open
          teamId={teamIdForLeaderImport}
          targetWorkspaceId={workspaceId}
          targetSectionId={leaderImportTargetSectionId}
          onClose={() => setLeaderImportTargetSectionId(null)}
          onImported={(payload) => applyLeaderCalendarImport(leaderImportTargetSectionId, payload)}
        />
      ) : null}

      {/* 클래스 선택 모달 */}
      {classPickerSectionId && (
        <div
          className="cp-modal-backdrop"
          onClick={(e) => {
            const targetEl = e.target instanceof Element ? e.target : (e.target as Node | null)?.parentElement;
            /** 튜토리얼 말풍선 "다음" 등은 바깥 클릭으로 취급하지 않는다. */
            if (targetEl?.closest('.cp-tutorial-root')) return;
            setClassPickerSectionId(null);
          }}
        >
          <div className="cp-modal cp-class-picker-modal" onClick={(e) => e.stopPropagation()}>
            <h3>클래스 등록</h3>
            <p className="page-desc">이 섹션 데이터를 적용받을 클래스를 선택하세요.</p>
            <div className="cp-program-color-class-picker-list">
              {classPickerAvailableGrades.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    className="cp-program-color-class-picker-item"
                    onClick={() => addClassToSection(classPickerSectionId, g.id)}
                  >
                    {parseClassName(g.name).title}
                  </button>
                ))}
            </div>
            {classPickerAvailableGrades.length === 0 && (
              <p className="page-desc">등록 가능한 클래스가 없습니다.</p>
            )}
            <div className="cp-modal-footer">
              {classPickerAvailableGrades.length === 0 && classSettingsModal ? (
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={() => {
                    setClassPickerSectionId(null);
                    classSettingsModal.openClassSettingsModal();
                  }}
                >
                  클래스 등록 바로가기
                </button>
              ) : null}
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setClassPickerSectionId(null)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 클래스 제거 확인 모달 */}
      {removeClassContext && (
        <div className="cp-modal-backdrop" onClick={() => setRemoveClassContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>클래스 제거</h3>
            <p className="page-desc">
              이 섹션에서 &quot;
              {grades.find((g) => g.id === removeClassContext.gradeId)
                ? parseClassName(
                    grades.find((g) => g.id === removeClassContext.gradeId)!.name
                  ).title
                : ''}
              &quot; 클래스를 제거할까요?
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setRemoveClassContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() =>
                  removeClassFromSection(removeClassContext.sectionId, removeClassContext.gradeId)
                }
              >
                제거
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 섹션 초기화 확인 모달 */}
      {resetSectionContext && (
        <div className="cp-modal-backdrop" onClick={() => setResetSectionContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 초기화</h3>
            <p className="page-desc">
              이 섹션의 학사일정 데이터(학년도·방학·공휴일·토·일 수업일)와 등록된 클래스를
              초기화할까요? 오른쪽 위 &quot;저장하기&quot;를 눌러야 서버에 반영됩니다.
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setResetSectionContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => resetSectionData(resetSectionContext)}
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 섹션 삭제 확인 모달 */}
      {deleteSectionContext && (
        <div className="cp-modal-backdrop" onClick={() => setDeleteSectionContext(null)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>섹션 삭제</h3>
            <p className="page-desc">
              이 섹션을 삭제할까요? 등록된 클래스 연결 정보가 삭제됩니다.
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => setDeleteSectionContext(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => removeSection(deleteSectionContext)}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteDay && (
        <div
          className="cp-modal-backdrop"
          onClick={() => {
            setPendingDeleteDay(null);
            setPendingDeleteSectionId(null);
          }}
        >
          <div
            className="cp-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>삭제</h3>
            <p className="page-desc">
              {formatDateShort(pendingDeleteDay.day)}
              {pendingDeleteDay.note ? ` ${pendingDeleteDay.note}` : ''}
              을(를) 삭제할까요?
            </p>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setPendingDeleteDay(null);
                  setPendingDeleteSectionId(null);
                }}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => handleDeleteDay(pendingDeleteDay)}
                disabled={loading}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {editingDay && (
        <div
          className="cp-modal-backdrop"
          onClick={() => {
            setEditingDay(null);
            setEditingSectionId(null);
          }}
        >
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>공휴일 수정</h3>
            <div className="form-row">
              <label>
                날짜
                <input
                  type="date"
                  value={editDayDate}
                  onChange={(e) => setEditDayDate(e.target.value)}
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                메모
                <input
                  type="text"
                  value={editDayNote}
                  onChange={(e) =>
                    setEditDayNote(
                      editingDay?.day_kind === 'class'
                        ? e.target.value
                        : limitHolidayNoteInput(e.target.value)
                    )
                  }
                  maxLength={editingDay?.day_kind === 'class' ? undefined : MAX_CALENDAR_HOLIDAY_NOTE_CHARS}
                  placeholder={
                    editingDay?.day_kind === 'class'
                      ? '예: 보강'
                      : `예: 3.1절 (최대 ${MAX_CALENDAR_HOLIDAY_NOTE_CHARS}자)`
                  }
                />
              </label>
            </div>
            <div className="cp-modal-footer">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={() => {
                  setEditingDay(null);
                  setEditingSectionId(null);
                }}
              >
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={handleSaveEditDay}
                disabled={loading}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSaveAllWithFixed()}
        disabled={loading || !isDirty}
        aria-label="저장하기"
        title="저장하기"
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>

      <AutoStatusToast state={saveToastState} />
    </section>
  );
};

export default AcademicCalendarPage;
